import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Search, Brain, Zap, Wrench, Bot } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';

interface EngineDetail {
  name: string;
  color: string;
  subtitle: string;
  description: string;
}

const VirtuousCycleGraphic: React.FC = () => {
  const [selectedEngine, setSelectedEngine] = useState<EngineDetail | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedEngine(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const engines = [
    {
      name: 'AutarLENS',
      full: 'Layered Evaluation and Navigation System',
      desc: 'Multi-agent deep analysis of your codebase. Language, CI/CD, testing, security, docs, dependencies.',
      description:
        "AutarLENS (Layered Evaluation and Navigation System) is the entry point for every repository Autar touches. It performs a deep, multi-agent analysis of a codebase, deploying specialized scanners in parallel to evaluate language ecosystems, CI/CD pipelines, testing strategies, security posture, documentation quality, dependency health, and AI tooling adoption. Each scanner operates independently, producing structured signals that feed directly into AutarGUARD and AutarTUNE for pattern matching and configuration recommendations. A full scan completes in minutes, not hours, giving teams an immediate, comprehensive profile of their repository's current state, strengths, and gaps. AutarLENS is designed to run on first connection and continuously thereafter, so the knowledge graph stays current as the codebase evolves. Think of it as the diagnostic that makes everything else in the Autar platform possible: before you can maintain, configure, or optimize, you need to deeply understand what you're working with.",
      tag: '▶ Entry Point',
      color: '#60a5fa',
      bgColor: 'rgba(96, 165, 250, 0.15)',
      tagBg: 'rgba(96, 165, 250, 0.1)',
      tagBorder: 'rgba(96, 165, 250, 0.2)',
      icon: <Search size={20} color="#60a5fa" />,
      position: 'top' as const,
    },
    {
      name: 'AutarGUARD',
      full: 'Graph-Unified Archetype Recognition & Diagnostics',
      desc: 'Scan signals populate the graph. Maintenance outcomes enrich it. Every interaction makes it smarter.',
      description:
        'AutarGUARD (Graph-Unified Archetype Recognition & Diagnostics) is a structured map of every meaningful signal across thousands of analyzed repositories. The knowledge graph captures languages, frameworks, CI/CD patterns, test strategies, deployment targets, team size, package versions and scale indicators that are linked together so that when a new repo is ingested, the graph can pattern-match its characteristics against known archetypes and extract the optimal configuration. It compares patterns in similar repos that are used successfully in production, rather than relying on generic templates or manual setup. AutarGUARD applies the collective intelligence of the entire ecosystem into a personalized, scale-aware SDLC prescription for any given codebase.',
      tag: '📊 Intelligence',
      color: '#a78bfa',
      bgColor: 'rgba(167, 139, 250, 0.15)',
      tagBg: 'rgba(167, 139, 250, 0.1)',
      tagBorder: 'rgba(167, 139, 250, 0.2)',
      icon: <Brain size={20} color="#a78bfa" />,
      position: 'right' as const,
    },
    {
      name: 'AutarTUNE',
      full: 'Tailored Usage and Normalization Engine',
      desc: "Queries the knowledge graph to surface the optimal config for your repo's archetype and scale.",
      description: `AutarTUNE (Tailored Usage and Normalization Engine) is the intelligence layer that turns Autar's knowledge graph into actionable configuration decisions. By analyzing patterns across thousands of repositories, it learns which tools, workflows, and settings correlate with success for specific repo archetypes, then surfaces those preferences as recommendations when a new repo is onboarded. For example, AutarTUNE recognizes that a large TypeScript monorepo with 50+ packages shares structural DNA with projects like Angular and Nx, so it recommends Bazel over Turborepo, strict trunk-based development over GitFlow, and integration-heavy test strategies over unit-only coverage. Rather than applying universal "best practices," AutarTUNE understands that the right configuration depends on context: team size, deployment target, dependency graph complexity, and ecosystem norms. It is the mechanism that makes Autar's recommendations feel like they came from an engineer who has already solved your exact problem at scale.`,
      tag: '🎯 Recommendations',
      color: '#34d399',
      bgColor: 'rgba(52, 211, 153, 0.15)',
      tagBg: 'rgba(52, 211, 153, 0.1)',
      tagBorder: 'rgba(52, 211, 153, 0.2)',
      icon: <Zap size={20} color="#34d399" />,
      position: 'bottom' as const,
    },
    {
      name: 'AutarMaTE',
      full: 'Maintenance Tasks Engine',
      desc: 'Executes fixes via branch, validate, reassess. Configurable trust from PR review to auto-merge.',
      description:
        'AutarMaTE (Maintenance Tasks Engine) handles every maintenance action through a disciplined, auditable workflow: it creates a dedicated branch, pushes changes, runs the full validation suite, and reassesses impact before ever surfacing the result to the user. This process is fully customizable to fit within your existing sprint cadence, whether that means slotting into two-week cycles, daily deploys, or trunk-based development. Trust levels let you dial the automation from conservative (create a PR for human review) all the way to fully autonomous (validate and auto-merge when all signals pass). Every PR and commit is documented the way a senior engineer would do it: clear descriptions of what was changed, why, what was considered, and what the validation results confirmed. The goal is that a teammate reviewing an AutarMaTE PR can follow the reasoning as easily as if another developer on the team wrote it.',
      tag: '🚀 Execution',
      color: '#fbbf24',
      bgColor: 'rgba(251, 191, 36, 0.15)',
      tagBg: 'rgba(251, 191, 36, 0.1)',
      tagBorder: 'rgba(251, 191, 36, 0.2)',
      icon: <Wrench size={20} color="#fbbf24" />,
      position: 'left' as const,
    },
  ];

  const autarAgentDetail: EngineDetail = {
    name: 'AutarAGENT',
    subtitle: 'Your AI-powered Virtual Dev',
    color: '#f472b6',
    description:
      "AutarAGENT sits alongside the entire cycle, acting as your always-available engineering partner. It interprets AutarLENS scan results in plain language, explains why AutarGUARD classified your repo the way it did, walks you through AutarTUNE's configuration recommendations, and guides you step-by-step through AutarMaTE's maintenance tasks. Think of it as a senior engineer who knows your entire codebase, never sleeps, and is always ready to pair. Whether you want to understand a critical vulnerability finding, debate a dependency upgrade strategy, or get hands-on help executing a complex migration, AutarAGENT is there. It's not a chatbot bolted on. It's the connective tissue that makes the other four components accessible to every developer, regardless of experience level.",
  };

  const positionStyles: Record<string, React.CSSProperties> = {
    top: { top: 0, left: '50%', transform: 'translateX(-50%)' },
    right: { top: '50%', right: -20, transform: 'translateY(-50%)' },
    bottom: { bottom: 0, left: '50%', transform: 'translateX(-50%)' },
    left: { top: '50%', left: -20, transform: 'translateY(-50%)' },
  };

  return (
    <div style={{ position: 'relative', width: 558, height: 558, margin: '0 auto' }}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 432,
          height: 432,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          border: '1.5px solid rgba(100, 116, 139, 0.2)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 432,
          height: 432,
          transform: 'translate(-50%, -50%)',
          animation: 'autar-rotate 12s linear infinite',
        }}
      >
        {[
          { top: -4, left: '50%', transform: 'translateX(-50%)', bg: '#60a5fa', shadow: '#60a5fa' },
          {
            bottom: -4,
            left: '50%',
            transform: 'translateX(-50%)',
            bg: '#fbbf24',
            shadow: '#fbbf24',
          },
          { top: '50%', left: -4, transform: 'translateY(-50%)', bg: '#a78bfa', shadow: '#a78bfa' },
          {
            top: '50%',
            right: -4,
            transform: 'translateY(-50%)',
            bg: '#34d399',
            shadow: '#34d399',
          },
        ].map((dot, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: dot.bg,
              boxShadow: `0 0 12px ${dot.shadow}, 0 0 24px ${dot.shadow}40`,
              ...dot,
            }}
          />
        ))}
      </div>

      {engines.map((engine) => (
        <button
          key={engine.name}
          type="button"
          onClick={() =>
            setSelectedEngine({
              name: engine.name,
              subtitle: engine.full,
              color: engine.color,
              description: engine.description,
            })
          }
          style={{
            position: 'absolute',
            width: 238,
            padding: 20,
            borderRadius: 16,
            background: 'rgba(15, 15, 25, 0.9)',
            border: '1px solid rgba(100, 116, 139, 0.15)',
            backdropFilter: 'blur(10px)',
            zIndex: 10,
            cursor: 'pointer',
            textAlign: 'left',
            ...positionStyles[engine.position],
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: engine.bgColor,
                flexShrink: 0,
              }}
            >
              {engine.icon}
            </div>
            <div>
              <div
                style={{ fontSize: 16, fontWeight: 700, color: engine.color, letterSpacing: -0.3 }}
              >
                {engine.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  opacity: 0.5,
                  color: '#fff',
                }}
              >
                {engine.full}
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'inline-block',
              marginTop: 10,
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.5,
              background: engine.tagBg,
              color: engine.color,
              border: `1px solid ${engine.tagBorder}`,
            }}
          >
            {engine.tag}
          </div>
        </button>
      ))}

      <button
        type="button"
        onClick={() => setSelectedEngine(autarAgentDetail)}
        style={{
          position: 'absolute',
          right: -100,
          top: 30,
          zIndex: 15,
          border: '1px solid #f472b699',
          background: '#f472b622',
          color: '#f472b6',
          borderRadius: 999,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.2,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Bot size={14} />
        AutarAGENT
      </button>

      {selectedEngine && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="engine-modal-title"
          onClick={() => setSelectedEngine(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.82)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            zIndex: 2000,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(760px, 100%)',
              background: '#0f172a',
              border: `1px solid ${selectedEngine.color}66`,
              borderRadius: 16,
              boxShadow: '0 24px 48px rgba(2, 6, 23, 0.45)',
              padding: 24,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div>
                <h3
                  id="engine-modal-title"
                  style={{ margin: 0, fontSize: 28, fontWeight: 800, color: selectedEngine.color }}
                >
                  {selectedEngine.name}
                </h3>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    color: '#94a3b8',
                  }}
                >
                  {selectedEngine.subtitle}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEngine(null)}
                style={{
                  background: '#1e293b',
                  color: '#e2e8f0',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
            <p
              style={{
                marginTop: 16,
                marginBottom: 0,
                fontSize: 16,
                lineHeight: 1.7,
                color: '#cbd5e1',
              }}
            >
              {selectedEngine.description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const AutarPlatformV2DiagramPage: React.FC = () => {
  return (
    <div
      style={{
        background: '#0a0a0f',
        minHeight: '100vh',
        color: '#ffffff',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`
        @keyframes autar-rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>

      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '70px 24px 0' }}>
        <Link to="/brand" style={{ color: '#94a3b8', fontSize: 14, textDecoration: 'none' }}>
          ← Product Concepts
        </Link>
      </div>

      <div style={{ textAlign: 'center', paddingTop: 12, paddingBottom: 40 }}>
        <h1
          style={{
            fontSize: 42,
            fontWeight: 800,
            letterSpacing: -1,
            background: 'linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 12,
          }}
        >
          The Autar Platform
        </h1>
        <p style={{ fontSize: 18, color: '#64748b', fontWeight: 500 }}>
          A Virtuous Cycle for Autonomous Repository Maintenance
        </p>
      </div>

      <div style={{ padding: '20px 0 80px', display: 'flex', justifyContent: 'center' }}>
        <VirtuousCycleGraphic />
      </div>

      <Footer />
    </div>
  );
};

export default AutarPlatformV2DiagramPage;
